import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useAppDispatch, RootState } from '../../store';
import apis from '../../utils/apis';
import logger from '../../utils/logger';
import { formatRoleNameForDisplay } from '../../utils/roleFormatter';
import AdminUserEdit from './AdminUserEdit';
import { adminUserPath } from './adminRoutes';
import PersonCell from './people/PersonCell';
import usePeopleDirectory from './people/usePeopleDirectory';
import TypeToConfirm from '../shared/TypeToConfirm';
import {
  revokeAccessPath,
  restoreAccessPath,
  revocationErrorMessage,
  reassignmentSummary,
  readActiveState,
} from './people/accessRevocation';
import { adminUserRouteId } from './userEdit/userEditModel';
import Badge from '../shared/TailwindComponents/Badge';
import Table from '../shared/TailwindComponents/Table';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import ListSurface from '../Settings/framework/ListSurface';
import Button from '../shared/TailwindComponents/Button';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import {
  PiTrashLight,
  PiPencilLight,
  PiSealCheckLight,
  PiLockKeyLight,
  PiUsersLight,
  PiProhibitLight,
  PiArrowCounterClockwiseLight,
  PiUserPlusLight,
} from 'react-icons/pi';
import PeopleForm from '../Management/Employees/PeopleForm';
import ImportUsersWizard from '../Import/ImportUsersWizard';
import { revokeUserFromAddon } from '../../store/actions/PlanActions';
import { showSidePanel, hideSidePanel } from '../../store/actions/sidePanelActions';
import { readErrorMessage, toErrorText } from '../../utils/errorMessage';

/**
 * The people in the organization - the Administration › Users section.
 *
 * The section is a flex column that never grows past its pane: the filter bar
 * is fixed chrome, and the table owns the only scroll region (`min-h-0`), so a
 * 400-person organization scrolls its list rather than the settings page.
 */

interface SeatInfo {
  seat_count: number;
  seats_used: number;
  seats_available: number;  // Kept for backwards compat - may be null for base plans
  seats_assigned?: number;  // New: derived count of active assignments
  plan_name: string | null;
  plan_type: string | null;
  can_invite: boolean;
  org_license_id?: number | null;
}

interface User {
  id: number;
  uuid?: string;
  email: string;
  first_name: string;
  last_name: string;
  role?: string;
  is_org_admin: boolean;
  is_active: boolean;
  invitation_status?: string;
  created_at: string;
  has_base_seat?: boolean;
}

interface AdminUsersProps {
  openInviteModal?: boolean;
  onCloseInvite?: () => void;
  openImportModal?: boolean;
  onCloseImport?: () => void;
}

const displayName = (user: User): string =>
  `${user.first_name} ${user.last_name}`.trim() || user.email;

const AdminUsersList: React.FC<AdminUsersProps> = ({ openInviteModal = false, onCloseInvite, openImportModal = false, onCloseImport }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  // `/admin/users` carries neither pictures nor uuids; the organization
  // directory carries both, and one request answers for the whole list.
  const { directory } = usePeopleDirectory();
  const avatarDirectory = directory.avatars;
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>({});
  const [isInviting, setIsInviting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [, setSeatInfo] = useState<SeatInfo | null>(null);
  const [baseOrgLicenseId, setBaseOrgLicenseId] = useState<number | null>(null);
  const [seatActionLoading, setSeatActionLoading] = useState<number | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
  const adminCount = users.filter(u => u.is_org_admin).length;

  // Users eligible for seat assignment (not admin, not already seated)
  const unseatedUsers = users.filter(u => !u.is_org_admin && !u.has_base_seat);
  const prevOpenInviteModalRef = React.useRef(false);
  const prevOpenImportModalRef = React.useRef(false);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (openImportModal && !prevOpenImportModalRef.current) {
      setIsImporting(true);
    }
    prevOpenImportModalRef.current = openImportModal;
  }, [openImportModal]);

  useEffect(() => {
    if (openInviteModal && !prevOpenInviteModalRef.current) {
      setIsInviting(true);
    }
    prevOpenInviteModalRef.current = openInviteModal;
  }, [openInviteModal]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await apis.get('/admin/users');
      if (response.data.success) {
        setUsers(response.data.data || []);
        setFilteredUsers(response.data.data || []);
        if (response.data.seat_info) {
          setSeatInfo(response.data.seat_info);
        }
        setBaseOrgLicenseId(
          response.data.base_org_license_id ?? response.data.seat_info?.org_license_id ?? null
        );
      } else {
        dispatch(showFeedbackModal({
          showModal: true,
          message: toErrorText(response.data.error) || t('Failed to fetch users'),
          modalType: 'error',
          duration: 4000
        }));
      }
    } catch (error: any) {
      logger.error('Error fetching users', { status: error?.response?.status });
      dispatch(showFeedbackModal({
        showModal: true,
        message: readErrorMessage(error, t('Failed to fetch users')),
        modalType: 'error',
        duration: 4000
      }));
    } finally {
      setLoading(false);
    }
  };

  /*
    THE STATUS FILTER NOW FILTERS.

    It offered Active / Inactive and did nothing: `handleFilterChange` was an
    empty function under a comment claiming the search box already applied it,
    which it never did. Harmless while everyone was active - and exactly wrong
    the moment revoking someone makes "show me who still has access" a question
    an admin actually asks.
  */
  useEffect(() => {
    const needle = searchTerm.trim().toLowerCase();
    const wanted = selectedFilters['Status'] || [];
    const filtered = users.filter((user) => {
      const matchesSearch = !needle
        || user.email.toLowerCase().includes(needle)
        || user.first_name.toLowerCase().includes(needle)
        || user.last_name.toLowerCase().includes(needle);
      if (!matchesSearch) return false;
      // No selection, or both, means no opinion - not "match nothing".
      if (!wanted.length || wanted.length > 1) return true;
      return wanted[0] === 'active' ? user.is_active !== false : user.is_active === false;
    });
    setFilteredUsers(filtered);
  }, [searchTerm, users, selectedFilters]);

  const handleSearchChange = (query: string) => {
    setSearchTerm(query);
  };

  const userFilters = [
    {
      id: 'Status',
      name: t('Status'),
      options: [
        { value: 'active', label: t('Active') },
        { value: 'revoked', label: t('Revoked') },
      ],
    },
  ];

  const handleFilterChange = (filterName: string, selectedValues: string[]) => {
    setSelectedFilters((prev) => ({ ...prev, [filterName]: selectedValues }));
  };

  const handleClearAllFilters = () => {
    setSearchTerm('');
    setSelectedFilters({});
  };

  const handleInviteCancel = () => {
    setIsInviting(false);
    onCloseInvite?.();
  };

  const handleInviteConfirm = () => {
    setIsInviting(false);
    fetchUsers();
    onCloseInvite?.();
  };

  const openAssignPanel = (targetUserIds: number[]) => {
    const names = targetUserIds.map(id => {
      const u = users.find(user => user.id === id);
      return u ? displayName(u) : String(id);
    });
    dispatch(showSidePanel({
      type: 'assignLicense',
      showSidePanel: true,
      size: 'md',
      icon: <PiSealCheckLight className="w-5 h-5 text-fg-muted" />,
      message: targetUserIds.length === 1 ? t('Assign license') : t('Assign seats'),
      subMsg: targetUserIds.length === 1
        ? t('Select a plan and optional add-ons for this user.')
        : t('Assign a base plan to {{count}} selected users.', { count: targetUserIds.length }),
      userIds: targetUserIds,
      userNames: names,
      onComplete: () => {
        setSelectedUserIds(new Set());
        fetchUsers();
        dispatch(hideSidePanel());
      },
    }));
  };

  const handleSelectUser = (id: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      const numId = Number(id);
      if (next.has(numId)) {
        next.delete(numId);
      } else {
        next.add(numId);
      }
      return next;
    });
  };

  const handleSelectAllUsers = () => {
    if (selectedUserIds.size === unseatedUsers.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(unseatedUsers.map(u => u.id)));
    }
  };

  const handleRowClick = (row: any) => {
    // The uuid when the directory knows it, the numeric id only until it does —
    // a row must stay clickable while the pictures are still on their way.
    history.push(adminUserPath(adminUserRouteId(row.rawUser, directory.uuidByUserId)));
  };

  const handleAssignSeat = (user: User) => {
    openAssignPanel([user.id]);
  };

  const revokeSeat = async (user: User) => {
    if (!baseOrgLicenseId) return;
    setSeatActionLoading(user.id);
    try {
      const ok = await dispatch(
        revokeUserFromAddon({ org_license_id: baseOrgLicenseId, user_id: user.id })
      );
      if (ok) {
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('Seat revoked from {{name}}', { name: displayName(user) }),
          modalType: 'success',
          duration: 3000,
        }));
        await fetchUsers();
      }
    } finally {
      setSeatActionLoading(null);
    }
  };

  const handleRevokeSeat = (user: User) => {
    if (!baseOrgLicenseId) return;
    if (user.is_org_admin) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Admins always have an implicit seat and cannot be revoked.'),
        modalType: 'info',
        duration: 4000,
      }));
      return;
    }
    dispatch(showModal({
      type: 'confirmation',
      showModal: true,
      title: t('Revoke seat'),
      message: t('Revoke {{name}}\'s seat?', { name: displayName(user) }),
      subText: t('They lose access to licensed features until a seat is reassigned.'),
      icon: <PiLockKeyLight className="w-6 h-6 text-warning-600" />,
      variant: 'warning',
      confirmLabel: t('Revoke seat'),
      onConfirm: () => {
        dispatch(hideModal());
        revokeSeat(user);
      },
    }));
  };

  const handleEditUser = (user: User) => {
    dispatch(showModal({
      showModal: true,
      type: 'adminEditUser',
      user,
      item: { adminCount },
      message: t('Edit user'),
      onConfirm: async (updatedUser: any) => {
        try {
          // If role changed, assign new role
          if (updatedUser.role && updatedUser.role !== user.role) {
            const roleResponse = await apis.post(`/admin/users/${user.id}/roles`, {
              role_name: updatedUser.role,
            });

            if (!roleResponse.data.success) {
              dispatch(showFeedbackModal({
                showModal: true,
                message: toErrorText(roleResponse.data.error) || t('Failed to assign role'),
                modalType: 'error',
                duration: 4000
              }));
              return;
            }
          }

          setUsers(
            users.map((u) =>
              u.id === user.id
                ? { ...u, role: updatedUser.role, is_org_admin: updatedUser.is_org_admin }
                : u
            )
          );
          dispatch(showFeedbackModal({
            showModal: true,
            message: t('User updated successfully'),
            modalType: 'success',
            duration: 3000
          }));
        } catch (error: any) {
          logger.error('Error updating user', { status: error?.response?.status });
          dispatch(showFeedbackModal({
            showModal: true,
            message: readErrorMessage(error, t('Failed to update user')),
            modalType: 'error',
            duration: 4000
          }));
        }
      }
    }));
  };

  const removeUser = async (userId: number) => {
    try {
      const response = await apis.delete(`/users/${userId}`);

      if (response.data.success) {
        setUsers(users.filter((u) => u.id !== userId));
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('User removed successfully'),
          modalType: 'success',
          duration: 3000
        }));
      } else {
        dispatch(showFeedbackModal({
          showModal: true,
          message: toErrorText(response.data.error) || t('Failed to remove user'),
          modalType: 'error',
          duration: 4000
        }));
      }
    } catch (error: any) {
      logger.error('Error removing user', { status: error?.response?.status });
      dispatch(showFeedbackModal({
        showModal: true,
        message: readErrorMessage(error, t('Failed to remove user')),
        modalType: 'error',
        duration: 4000
      }));
    }
  };

  const handleRemoveUser = (user: User) => {
    dispatch(showModal({
      type: 'confirmation',
      showModal: true,
      title: t('Remove person'),
      message: t('Remove {{name}} from the organization?', { name: displayName(user) }),
      subText: t('They lose access immediately. This cannot be undone.'),
      icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
      variant: 'danger',
      confirmLabel: t('Remove'),
      onConfirm: () => {
        dispatch(hideModal());
        removeUser(user.id);
      },
    }));
  };

  /*
    DELETE PERMANENTLY - the irreversible, full erase of a single user.

    Unlike Revoke (reversible) and Remove, this wipes the user everywhere: strips
    them from every collaborator list, deletes their own rows, removes them from
    Auth0 and SQL, and deletes their profile picture. Firm/shared records they
    authored are reassigned to another admin, not deleted. We fetch a preview
    first (so the admin sees exactly what will happen) and require typing the
    user's EMAIL to confirm - the backend enforces the same match.
  */
  const runHardDelete = async (user: User) => {
    const response = await apis.post(`/api/users/admin/${user.id}/hard-delete`, {
      confirm_email: user.email,
      confirm: true,
    });
    if (!response?.data?.success) {
      throw new Error(toErrorText(response?.data?.error) || t('Failed to delete user'));
    }
    setUsers((prev) => prev.filter((u) => u.id !== user.id));
    dispatch(hideModal());
    dispatch(showFeedbackModal({
      showModal: true,
      modalType: 'success',
      message: t('{{name}} was permanently deleted.', { name: displayName(user) }),
      duration: 4000,
    }));
  };

  const handleHardDelete = async (user: User) => {
    let preview: any = null;
    try {
      const res = await apis.get(`/api/users/admin/${user.id}/delete-preview`);
      preview = res?.data?.data ?? null;
    } catch (error: any) {
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: 'error',
        message: readErrorMessage(error, t('Could not load the deletion preview.')),
        duration: 5000,
      }));
      return;
    }

    if (preview && preview.has_alternate_admin === false) {
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: 'error',
        message: t('Assign another admin before deleting this user — their firm records need an owner.'),
        duration: 6000,
      }));
      return;
    }

    const consequences: React.ReactNode[] = [
      t('They are removed from Auth0 and cannot sign in again.'),
      t('Their own data (memberships, sessions, notifications, settings) is deleted.'),
    ];
    if (preview?.collaborator_rows_removed > 0) {
      consequences.push(t('Removed from {{count}} shared item(s) they collaborated on.', { count: preview.collaborator_rows_removed }));
    }
    if (preview?.records_reassigned > 0) {
      consequences.push(t('{{count}} firm record(s) they own are reassigned to another admin (not deleted).', { count: preview.records_reassigned }));
    }

    dispatch(showModal({
      type: 'custom',
      showModal: true,
      message: t('Delete permanently'),
      icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
      content: (
        <TypeToConfirm
          phrase={user.email}
          inputLabel={t('Type the person’s email to confirm')}
          description={t('{{name}} and all of their own data will be permanently deleted. This cannot be undone.', { name: displayName(user) })}
          consequences={consequences}
          reassurance={t('The firm’s shared work is preserved — records they authored are reassigned to another admin.')}
          confirmLabel={t('Delete permanently')}
          onConfirm={() => runHardDelete(user)}
          onCancel={() => dispatch(hideModal())}
        />
      ),
    }));
  };

  /*
    REVOKE / RESTORE ACCESS - the reversible sibling of Remove.

    Remove (below) deletes the person. This deactivates them: they lose access
    immediately, their matters and tasks are reassigned to the org admin, their
    licence seat is released, and every request they make afterwards is refused
    with `ACCOUNT_REVOKED`. All of it undoable by restoring.

    Addressed by PERSON UUID, which `/admin/users` does not send - the uuid
    comes from `/org-people/people` through `usePeopleDirectory`. If it has not
    arrived, or this person has none, the action is DISABLED rather than
    optimistic: building `/people/undefined/revoke-access` would produce a 404
    that reads like a backend fault. (Exactly the bug the table deep-dive had.)
  */
  const [accessActionLoading, setAccessActionLoading] = useState<number | null>(null);

  const personUuidFor = (user: User): string | undefined =>
    user.uuid || directory.uuidByUserId.get(user.id);

  const applyActiveState = (userId: number, isActive: boolean) => {
    // Update from the response before refetching: `/admin/users` is a separate
    // payload from the one the backend just wrote, so the badge should not wait
    // on it to tell the truth.
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, is_active: isActive } : u)));
    setFilteredUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, is_active: isActive } : u)));
  };

  const runAccessChange = async (user: User, revoke: boolean) => {
    const personUuid = personUuidFor(user);
    if (!personUuid) return;

    setAccessActionLoading(user.id);
    try {
      const response = await apis.post(
        revoke ? revokeAccessPath(personUuid) : restoreAccessPath(personUuid),
      );
      const body = response.data || {};
      if (body.success === false) throw { response: { status: response.status, data: body } };

      const nextActive = readActiveState(body);
      applyActiveState(user.id, nextActive === null ? !revoke : nextActive);

      const reassigned = revoke ? reassignmentSummary(body.data?.metrics, t) : null;
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: 'success',
        message: revoke
          ? t('Access revoked for {{name}}', { name: displayName(user) })
          : t('Access restored for {{name}}', { name: displayName(user) }),
        subMsg: reassigned || undefined,
        duration: 5000,
      }));
      dispatch(hideModal());
      fetchUsers();
    } catch (error: any) {
      logger.error('Error changing person access', {
        revoke, status: error?.response?.status, code: error?.response?.data?.code,
      });
      // The 409s are answers, not outages - `NO_ALTERNATE_ADMIN` in particular
      // tells the admin what to do first, so it must not become "failed".
      dispatch(showFeedbackModal({
        showModal: true,
        modalType: 'error',
        message: revocationErrorMessage(error, t),
        duration: 6000,
      }));
      // The dialog stays open on failure: assigning another admin and trying
      // again is the expected next step.
      throw error;
    } finally {
      setAccessActionLoading(null);
    }
  };

  const handleRevokeAccess = (user: User) => {
    dispatch(showModal({
      type: 'custom',
      showModal: true,
      message: t('Revoke access'),
      icon: <PiProhibitLight className="w-6 h-6 text-danger-600" />,
      content: (
        <TypeToConfirm
          phrase={displayName(user)}
          inputLabel={t('Type the person’s name to confirm')}
          description={t('{{name}} will lose access to PONS immediately.', { name: displayName(user) })}
          consequences={[
            t('They cannot sign in, and any open session stops working.'),
            t('Their matters and tasks are reassigned to you.'),
            t('Their licence seat is released back to the organization.'),
          ]}
          reassurance={t('This is reversible — you can restore their access at any time, and nothing is deleted.')}
          confirmLabel={t('Revoke access')}
          onConfirm={() => runAccessChange(user, true)}
          onCancel={() => dispatch(hideModal())}
        />
      ),
    }));
  };

  /* Restoring is not destructive and needs no typed confirmation - but it does
     reassign nothing back, so say that plainly before they press it. */
  const handleRestoreAccess = (user: User) => {
    dispatch(showModal({
      type: 'confirmation',
      showModal: true,
      title: t('Restore access'),
      message: t('Give {{name}} access to PONS again?', { name: displayName(user) }),
      subText: t('They can sign in immediately. Work that was reassigned while they were revoked stays where it is, and a licence seat is not assigned automatically.'),
      icon: <PiArrowCounterClockwiseLight className="w-6 h-6 text-info-600" />,
      variant: 'info',
      confirmLabel: t('Restore access'),
      onConfirm: () => { void runAccessChange(user, false).catch(() => {}); },
      onClose: () => { dispatch(hideModal()); },
    }));
  };

  const columns = ['Name', 'Email', 'Role', 'Seat', 'Status', 'Actions'];

  const tableData = filteredUsers.map((user) => ({
    id: String(user.id),
    'Name': (
      <PersonCell
        person={user}
        directory={avatarDirectory}
        name={displayName(user)}
        subtitle={
          user.is_org_admin ? (
            <span className="text-champagne">{t('Administrator')}</span>
          ) : undefined
        }
      />
    ),
    'Email': (
      <span className="block truncate text-sm text-fg-body" title={user.email}>
        {user.email}
      </span>
    ),
    'Role': (
      <span className="block truncate text-sm text-fg-body">
        {user.role ? formatRoleNameForDisplay(user.role) : '—'}
      </span>
    ),
    'Seat': (
      user.is_org_admin ? (
        <Badge color="blue">{t('Admin')}</Badge>
      ) : user.has_base_seat ? (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-success-700 dark:text-success-300">
          <PiSealCheckLight className="h-4 w-4" />
          {t('Seated')}
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-warning-700 dark:text-warning-300">
          <PiLockKeyLight className="h-4 w-4" />
          {t('No seat')}
        </span>
      )
    ),
    /* Three states, not two: an inactive user who is still a PENDING invite
       (invited, never accepted) is not "Revoked" - only an admin-revoked
       user is. "Revoked" rather than "Inactive" because an admin acted and
       the word has to match the action in the menu. */
    'Status': (
      user.is_active ? (
        <Badge color="green">{t('Active')}</Badge>
      ) : (user.invitation_status === 'pending' || user.invitation_status === 'invited') ? (
        <Badge color="yellow">{t('Pending')}</Badge>
      ) : (
        <Badge color="red">{t('Revoked')}</Badge>
      )
    ),
    'Actions': null,
    rawUser: user,
  }));

  const userActions = [
    {
      label: t('Edit'),
      onClick: (row: any) => handleEditUser(row.rawUser),
      icon: <PiPencilLight className="h-4 w-4" />
    },
    {
      label: t('Assign license'),
      onClick: (row: any) => handleAssignSeat(row.rawUser),
      icon: <PiSealCheckLight className="h-4 w-4 text-success-600" />,
      hidden: (row: any) => row.rawUser.is_org_admin || row.rawUser.has_base_seat === true,
    },
    {
      label: t('Manage license'),
      onClick: (row: any) => openAssignPanel([row.rawUser.id]),
      icon: <PiSealCheckLight className="h-4 w-4" />,
      hidden: (row: any) => row.rawUser.is_org_admin || row.rawUser.has_base_seat !== true,
    },
    {
      label: t('Revoke seat'),
      onClick: (row: any) => handleRevokeSeat(row.rawUser),
      icon: <PiLockKeyLight className="h-4 w-4 text-warning-600" />,
      hidden: (row: any) => row.rawUser.is_org_admin || row.rawUser.has_base_seat !== true,
      disabled: (row: any) => seatActionLoading === row.rawUser.id,
    },
    /*
      REVOKE and RESTORE are mutually exclusive on the person's state, so each
      hides when the other applies and the menu never offers both. Neither is
      offered for yourself - the backend refuses it with `CANNOT_REVOKE_SELF`,
      and an action that can only fail should not be in the menu.

      Disabled without a person uuid: `/admin/users` does not carry one and the
      directory that does may not have arrived yet.
    */
    {
      label: t('Revoke access'),
      onClick: (row: any) => handleRevokeAccess(row.rawUser),
      icon: <PiProhibitLight className="h-4 w-4 text-danger-600" />,
      isDestructive: true,
      hidden: (row: any) => row.rawUser.is_active === false
        || row.rawUser.id === userInfo?.UserUID,
      disabled: (row: any) => accessActionLoading === row.rawUser.id
        || !personUuidFor(row.rawUser),
    },
    {
      label: t('Restore access'),
      onClick: (row: any) => handleRestoreAccess(row.rawUser),
      icon: <PiArrowCounterClockwiseLight className="h-4 w-4 text-info-600" />,
      hidden: (row: any) => row.rawUser.is_active !== false,
      disabled: (row: any) => accessActionLoading === row.rawUser.id
        || !personUuidFor(row.rawUser),
    },
    {
      label: t('Remove'),
      onClick: (row: any) => handleRemoveUser(row.rawUser),
      icon: <PiTrashLight className="h-4 w-4 text-danger-600" />,
      isDestructive: true,
      disabled: (row: any) => row.rawUser.id === userInfo?.UserUID
    },
    {
      label: t('Delete permanently'),
      onClick: (row: any) => { void handleHardDelete(row.rawUser); },
      icon: <PiTrashLight className="h-4 w-4 text-danger-600" />,
      isDestructive: true,
      // Never for yourself (the backend refuses it), and an org needs another
      // admin to inherit the firm records.
      hidden: (row: any) => row.rawUser.id === userInfo?.UserUID,
    },
  ];

  if (isInviting) {
    return (
      <div className="flex h-full min-h-0 w-full flex-col">
        <PeopleForm
          onSuccess={handleInviteConfirm}
          onCancel={handleInviteCancel}
          inviteType="admin"
        />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <FilterBarWithTabs
        itemCount={filteredUsers.length}
        totalItemCount={users.length}
        itemCountLabel="users"
        filters={userFilters}
        selectedFilters={selectedFilters}
        onFilterChange={handleFilterChange}
        onSearchChange={handleSearchChange}
        onClearAllFilters={handleClearAllFilters}
        noParentPadding
        selectionCount={selectedUserIds.size}
        actions={selectedUserIds.size > 0 ? [
          {
            label: t('Assign seats'),
            onClick: () => openAssignPanel(Array.from(selectedUserIds)),
            icon: <PiSealCheckLight className="h-4 w-4" />,
          },
        ] : []}
        // Invite / Import live here (always-visible controls) because the
        // Settings shell mounts this component without the legacy AdminPage
        // header that used to host them. `actions` is only the bulk-selection
        // menu, so it can't carry these. Backend still gates by permission + seats.
        additionalControls={(
          <Button variant="primary" size="small" onClick={() => setIsInviting(true)}>
            <PiUserPlusLight className="h-4 w-4 mr-1.5" />
            {t('Invite user')}
          </Button>
        )}
      />

      {/* The list is the only scroll region - the table owns it, so the
          settings page itself never scrolls under its own header. */}
      <ListSurface>
        <Table
          tableId="admin-users"
          columns={columns}
          data={tableData}
          actions={userActions}
          onRowClick={handleRowClick}
          isLoading={loading}
          enableSelection={unseatedUsers.length > 0}
          selectedIds={Array.from(selectedUserIds).map(String)}
          onSelect={handleSelectUser}
          onSelectAll={handleSelectAllUsers}
          total_items={users.length}
          total_showing_items={filteredUsers.length}
          setIsShowing={true}
          showingLabel={t('users')}
          emptyState={{
            icon: <PiUsersLight />,
            headline: searchTerm ? t('No people match that search') : t('No people yet'),
            subtext: searchTerm
              ? t('Try a different name or email address.')
              : t('Invite colleagues to give them access to your organization.'),
          }}
          defaultColumnWidths={{ Name: 240, Email: 240, Role: 160, Seat: 120, Status: 110 }}
          min="min-h-0"
          max="max-h-full"
        />
      </ListSurface>

      <ImportUsersWizard
        isOpen={isImporting}
        onClose={() => { setIsImporting(false); onCloseImport?.(); }}
        onComplete={() => { fetchUsers(); setIsImporting(false); onCloseImport?.(); }}
      />
    </div>
  );
};

/**
 * The section as the settings shell mounts it.
 *
 * `/settings/admin-users` is the list; `/settings/admin-users/:detailId` is one
 * person, rendered INSIDE the same shell rather than at a top-level
 * `/admin/users/:id` that drops the rail. The shell routes on the section id
 * and knows nothing about details - this is where a section decides it has
 * one, which is why no layout file has to change to add another.
 *
 * `useParams` is empty when there is no such route match (the legacy `/admin`
 * page renders this component directly), so the list is the default.
 */
const AdminUsers: React.FC<AdminUsersProps> = (props) => {
  const { detailId } = useParams<{ detailId?: string }>();
  if (detailId) return <AdminUserEdit embedded userId={detailId} />;
  return <AdminUsersList {...props} />;
};

export default AdminUsers;
